import { useState, useEffect, useRef } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

/**
 * Weekly reports submitted by this client (organizations/{orgId}/reports, uid == client).
 * Sorted by createdOn descending (client-side) to avoid composite index requirements.
 */
export function useClientWeeklyReports({ clientId, organizationId }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);

  const isValid = Boolean(clientId && organizationId);

  useEffect(() => {
    if (!isValid) {
      setReports([]);
      setLoading(false);
      setError(null);
      return;
    }
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    setLoading(true);
    setError(null);

    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("reports")
      .where("uid", "==", clientId);

    unsubscribeRef.current = ref.onSnapshot(
      (snapshot) => {
        const list = snapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            ...d,
            createdOn: d.createdOn?.toDate?.() ?? d.createdOn,
            startDate: d.startDate?.toDate?.() ?? d.startDate,
            endDate: d.endDate?.toDate?.() ?? d.endDate,
          };
        });
        list.sort((a, b) => {
          const ta = a.createdOn instanceof Date ? a.createdOn.getTime() : 0;
          const tb = b.createdOn instanceof Date ? b.createdOn.getTime() : 0;
          return tb - ta;
        });
        setReports(list.slice(0, 36));
        setLoading(false);
      },
      (err) => {
        setError(err);
        setReports([]);
        setLoading(false);
      }
    );

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [clientId, organizationId, isValid]);

  return { reports, loading, error };
}
